/* eslint-disable @typescript-eslint/naming-convention */
import { Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import {
	AccordionComponent,
	AlertComponent,
	OliveRoundelComponent,
	PoweredByOnecardComponent,
} from '@woolworthsnz/styleguide';

import { isPlatformBrowser } from '@angular/common';
import {
	ArticleComponent,
	Carousel2DCComponent,
	FormCheckboxComponent,
	FormDropdownComponent,
	FormEmailComponent,
	FormHiddenComponent,
	FormMultilineComponent,
	FormNumberComponent,
	FormSingleLineComponent,
	FormSubmitComponent,
	ImageNavigationComponent,
	ProductGridComponent as DcProductGridComponent,
} from '@woolworthsnz/dynamic-content';
import { CategoryNavComponent, GridContainerComponent } from '@woolworthsnz/recipe';

@Injectable({
	providedIn: 'root',
})
export class CustomElementsFactoryService {
	static asyncShoreCoffeCardInit = false;
	static asyncShoreGridInit = false;
	static asyncShoreGiftingInit = false;

	constructor(@Inject(PLATFORM_ID) private platformId: object, private injector: Injector) {}

	/**
	/* NOTE: when defining new custom elements name the tag differently to the one in angular to avoid weird side effects
	/* Suffixing `-element` is probably safe
	/* E.g. angular component <cdx-olive-roundel> vs custom element <olive-element>	 *
	 * @param injector
	 * @param isBrowser
	 */
	static async CreateCustomElements(injector: Injector, isBrowser: boolean): Promise<void> {
		if (isBrowser) {
			const { createCustomElement } = require('@angular/elements');

			if (!customElements.get('olive-element')) {
				customElements.define(
					'olive-element',
					createCustomElement(OliveRoundelComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('alert-element')) {
				customElements.define(
					'alert-element',
					createCustomElement(AlertComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('powered-by-onecard-element')) {
				customElements.define(
					'powered-by-onecard-element',
					createCustomElement(PoweredByOnecardComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('recipe-grid-element')) {
				customElements.define(
					'recipe-grid-element',
					createCustomElement(GridContainerComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('recipe-nav')) {
				customElements.define(
					'recipe-nav',
					createCustomElement(CategoryNavComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('dc-form-submit-element')) {
				customElements.define(
					'dc-form-submit-element',
					createCustomElement(FormSubmitComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('dc-form-email-element')) {
				customElements.define(
					'dc-form-email-element',
					createCustomElement(FormEmailComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('dc-form-number-element')) {
				customElements.define(
					'dc-form-number-element',
					createCustomElement(FormNumberComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('dc-form-checkbox-element')) {
				customElements.define(
					'dc-form-checkbox-element',
					createCustomElement(FormCheckboxComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('dc-form-dropdown-element')) {
				customElements.define(
					'dc-form-dropdown-element',
					createCustomElement(FormDropdownComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('dc-form-hidden-element')) {
				customElements.define(
					'dc-form-hidden-element',
					createCustomElement(FormHiddenComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('dc-form-multilinetext')) {
				customElements.define(
					'dc-form-multilinetext',
					createCustomElement(FormMultilineComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('dc-form-singlelinetext')) {
				customElements.define(
					'dc-form-singlelinetext',
					createCustomElement(FormSingleLineComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('article-element')) {
				customElements.define(
					'article-element',
					createCustomElement(ArticleComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('carousel-element')) {
				customElements.define(
					'carousel-element',
					createCustomElement(Carousel2DCComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('accordion-element')) {
				customElements.define(
					'accordion-element',
					createCustomElement(AccordionComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('dc-product-grid-element')) {
				customElements.define(
					'dc-product-grid-element',
					createCustomElement(DcProductGridComponent, {
						injector,
					})
				);
			}

			if (!customElements.get('image-navigation-element')) {
				customElements.define(
					'image-navigation-element',
					createCustomElement(ImageNavigationComponent, {
						injector,
					})
				);
			}
		}
	}

	/**
	 * This will create the custom elements if we're running in browser mode.
	 * Custom elements are not supported with server side rendering (SSR)
	 */
	bootstrapCustomElements(): Promise<void> {
		return CustomElementsFactoryService.CreateCustomElements(this.injector, isPlatformBrowser(this.platformId));
	}
}
